import React, { useCallback, useEffect, useState } from 'react';
import api, { getUserAvatarUrl } from '../../utils/api';
import { todayString } from '../../utils/appDateTime';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { CheckCircle, XCircle, Clock, AlertCircle, Save, Timer, Ban } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

function employeeInitials(name) {
  return (name || 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present', icon: CheckCircle },
  { value: 'absent', label: 'Absent', icon: XCircle },
  { value: 'half_day', label: 'Half-Day', icon: Clock },
  { value: 'leave', label: 'Leave (Paid)', icon: AlertCircle },
  { value: 'lwp', label: 'LWP', icon: Ban },
  { value: 'late', label: 'Late', icon: Timer },
];

function StatusButtons({ employeeId, attendance, isFutureDate, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {STATUS_OPTIONS.map((opt) => (
        <Button
          key={opt.value}
          type="button"
          variant={attendance[employeeId] === opt.value ? 'default' : 'outline'}
          size="sm"
          disabled={isFutureDate}
          title={opt.label}
          onClick={() => onChange(employeeId, opt.value)}
          className={
            attendance[employeeId] === opt.value
              ? opt.value === 'present'
                ? 'bg-green-600'
                : opt.value === 'absent'
                  ? 'bg-red-600'
                  : opt.value === 'half_day'
                    ? 'bg-amber-600'
                    : opt.value === 'late'
                      ? 'bg-orange-600'
                      : opt.value === 'lwp'
                        ? 'bg-purple-600'
                        : 'bg-blue-600'
              : 'bg-muted text-muted-foreground'
          }
        >
          <opt.icon className="w-4 h-4" />
        </Button>
      ))}
    </div>
  );
}

function formatLoadError(error) {
  const message = error?.response?.data?.message;
  const status = error?.response?.status;
  if (message) {
    return status ? `${message} (HTTP ${status})` : message;
  }
  if (status) {
    return `Failed to load employees (HTTP ${status})`;
  }
  return error?.message || 'Failed to load employees';
}

function PaginationBar({ meta, page, setPage }) {
  if (meta.pages <= 1) return null;
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4">
      <p className="text-sm text-muted-foreground">
        Page {meta.page} of {meta.pages} ({meta.total} employees)
      </p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
        <Button variant="outline" size="sm" disabled={page >= meta.pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </div>
    </div>
  );
}

export default function CrmEmployeeAttendance() {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState(todayString());
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ page: 1, limit: 50, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [warning, setWarning] = useState(null);
  const [currentPeriod, setCurrentPeriod] = useState(null);

  const today = todayString();
  const isFutureDate = selectedDate > today;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/attendance', {
        params: { date: selectedDate, page, limit: 50 },
      });
      const payload = res.data?.data ?? {};
      const list = payload.employees ?? [];
      setEmployees(list);
      setMeta(payload.meta ?? { page: 1, limit: 50, pages: 1, total: list.length });
      setWarning(payload.warning || null);
      setCurrentPeriod(payload.current_period || null);
      const map = {};
      list.forEach((e) => {
        map[e.employee_id] = e.status || 'absent';
      });
      setAttendance(map);
    } catch (e) {
      setEmployees([]);
      setWarning(null);
      toast({
        title: 'Error',
        description: formatLoadError(e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, page, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = (employeeId, status) => {
    setAttendance((prev) => ({ ...prev, [employeeId]: status }));
  };

  const handleSave = async () => {
    if (isFutureDate) {
      toast({ title: 'Future date', description: 'Cannot mark attendance for future dates', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const records = Object.entries(attendance).map(([employee_id, status]) => ({
        employee_id: Number(employee_id),
        status,
      }));
      await api.post('/crm/attendance/mark', { date: selectedDate, records });
      toast({ title: 'Saved', description: 'Employee attendance recorded.' });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: formatLoadError(e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Employee Attendance</h1>
          <p className="text-muted-foreground">Mark daily attendance for CRM team members</p>
          {currentPeriod?.period_start && (
            <p className="text-sm text-muted-foreground mt-1">
              Current salary cycle: {currentPeriod.period_start} – {currentPeriod.period_end}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center w-full sm:w-auto">
          <DateInput
            value={selectedDate}
            max={today}
            onChange={(v) => { setSelectedDate(v); setPage(1); }}
            className="w-full sm:w-auto"
          />
          <Button onClick={handleSave} disabled={saving || isFutureDate || loading} className="w-full sm:w-auto">
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daily attendance</CardTitle>
        </CardHeader>
        <CardContent>
          {warning ? (
            <p className="text-sm text-amber-600 mb-4">{warning}</p>
          ) : null}
          {loading ? <p className="text-muted-foreground">Loading…</p> : null}
          {!loading && employees.length === 0 ? (
            <p className="text-muted-foreground">No employees found.</p>
          ) : null}

          {!loading && employees.length > 0 ? (
            <div className="space-y-3 sm:hidden">
              {employees.map((e) => (
                <div key={e.employee_id} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <Avatar className="h-10 w-10 shrink-0">
                      {e.avatar ? (
                        <AvatarImage src={getUserAvatarUrl(e.avatar)} alt={e.name} className="object-cover" />
                      ) : null}
                      <AvatarFallback className="bg-blue-500/20 text-blue-600 font-semibold text-sm">
                        {employeeInitials(e.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{e.name}</p>
                      <p className="text-sm text-muted-foreground">{e.employee_code || '—'}</p>
                      {e.role_name && <Badge variant="outline" className="mt-1 text-xs">{e.role_name}</Badge>}
                    </div>
                  </div>
                  <StatusButtons
                    employeeId={e.employee_id}
                    attendance={attendance}
                    isFutureDate={isFutureDate}
                    onChange={handleStatusChange}
                  />
                </div>
              ))}
            </div>
          ) : null}

          {!loading && employees.length > 0 ? (
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((e) => (
                    <TableRow key={e.employee_id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8 shrink-0">
                            {e.avatar ? (
                              <AvatarImage src={getUserAvatarUrl(e.avatar)} alt={e.name} className="object-cover" />
                            ) : null}
                            <AvatarFallback className="bg-blue-500/20 text-blue-600 font-semibold text-xs">
                              {employeeInitials(e.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span>{e.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{e.employee_code || '—'}</TableCell>
                      <TableCell>{e.role_name || '—'}</TableCell>
                      <TableCell>
                        <StatusButtons
                          employeeId={e.employee_id}
                          attendance={attendance}
                          isFutureDate={isFutureDate}
                          onChange={handleStatusChange}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}

          <PaginationBar meta={meta} page={page} setPage={setPage} />
        </CardContent>
      </Card>
    </div>
  );
}
